<template>
    <div>
        <transition name="modal">
            <div class="bast-modal-mask" @click="$emit('close')">
                <div class="bast-modal-container" role="dialog" aria-modal="true" aria-labelledby="bast-preview-title" @click.stop>
                    <!-- Header -->
                    <div class="bast-header">
                        <div class="bast-heading">
                            <h6 id="bast-preview-title" class="bast-title">Preview BAST</h6>
                            <div class="bast-meta">
                                <div class="bast-meta-item">
                                    <span class="bast-meta-label">Kode BAST</span>
                                    <strong class="bast-meta-value">{{ bastNo || '-' }}</strong>
                                </div>
                                <span class="bast-meta-divider"></span>
                                <div class="bast-meta-item">
                                    <span class="bast-meta-label">Jumlah QR</span>
                                    <strong class="bast-meta-value">{{ formatCount(qty) }}</strong>
                                </div>
                            </div>
                        </div>
                        <button type="button" class="btn-close-bast" aria-label="Tutup" @click="$emit('close')">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>

                    <!-- Viewer -->
                    <div class="bast-body">
                        <div class="bast-viewer">
                            <div v-if="status === 'loading'" class="bast-skeleton" aria-hidden="true">
                                <div class="skeleton-row">
                                    <div class="skeleton-bar skeleton-bar--logo"></div>
                                    <div class="skeleton-bar skeleton-bar--mark"></div>
                                </div>
                                <div class="skeleton-stack">
                                    <div class="skeleton-bar skeleton-bar--title"></div>
                                    <div class="skeleton-bar skeleton-bar--subtitle"></div>
                                </div>
                                <div class="skeleton-divider"></div>
                                <div class="skeleton-stack skeleton-stack--end">
                                    <div class="skeleton-bar skeleton-bar--date"></div>
                                </div>
                                <div class="skeleton-stack">
                                    <div class="skeleton-bar skeleton-bar--label"></div>
                                    <div class="skeleton-bar skeleton-bar--wide"></div>
                                    <div class="skeleton-bar skeleton-bar--short"></div>
                                    <div class="skeleton-bar skeleton-bar--medium"></div>
                                </div>
                            </div>

                            <div v-else-if="status === 'failed'" class="bast-error">
                                <span class="error-icon"><i class="fas fa-exclamation"></i></span>
                                <div class="error-message">
                                    <h6 class="error-title">Dokumen gagal dimuat</h6>
                                    <p class="error-desc">Terjadi kendala saat memuat BAST. Periksa koneksi lalu coba lagi.</p>
                                </div>
                                <button type="button" class="btn-bast-secondary" @click="loadDocument">Coba Lagi</button>
                            </div>

                            <div v-else class="bast-paper-wrapper">
                                <!-- 800px paper is scaled by 0.94 so 100% fits the 752px viewer width -->
                                <div class="bast-paper" :style="{ zoom: zoom * 0.0094 }">
                                    <surat-pengantar :data="receipt" />
                                </div>
                            </div>
                        </div>

                        <div v-if="status === 'loading'" class="bast-loading-badge" role="status">
                            <span class="loading-spinner"></span>
                            <span>Memuat dokumen...</span>
                        </div>

                        <!-- Floating zoom control -->
                        <div v-if="status !== 'failed'" class="bast-zoom" :class="{ 'bast-zoom--disabled': !isLoaded }">
                            <button type="button" class="btn-zoom" aria-label="Perkecil" :disabled="!isLoaded || zoom <= ZOOM_MIN" @click="handleZoom(-ZOOM_STEP)">&minus;</button>
                            <span class="zoom-value">{{ zoom }}%</span>
                            <button type="button" class="btn-zoom" aria-label="Perbesar" :disabled="!isLoaded || zoom >= ZOOM_MAX" @click="handleZoom(ZOOM_STEP)">+</button>
                            <span class="zoom-separator">|</span>
                            <span class="page-value">1 / 1</span>
                        </div>
                    </div>

                    <!-- File info + actions -->
                    <div class="bast-footer">
                        <div class="file-info">
                            <span class="file-name">{{ filename }}</span>
                            <template v-if="isLoaded">
                                <span class="file-separator">&bull;</span>
                                <span class="file-size">{{ formatFileSize(pdfBlob.size) }}</span>
                            </template>
                        </div>
                        <div class="bast-actions">
                            <button type="button" class="btn-bast-secondary" @click="$emit('close')">Tutup</button>
                            <button type="button" class="btn-bast-primary" :disabled="!isLoaded" @click="handleDownload">
                                <i class="fas fa-download"></i>Download PDF
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Off-screen copy used only to render the PDF at its print width -->
        <div class="bast-pdf-source" aria-hidden="true">
            <div ref="pdfSource" class="bast-pdf-page">
                <surat-pengantar :data="receipt" />
            </div>
        </div>

        <transition name="toast-slide">
            <div v-if="showSuccessToast" class="top-right-toast" role="status">
                <div class="toast-icon-wrapper">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="12" fill="#22C55E"/>
                        <path d="M7 12.5L10.2 15.7L17 8.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <div class="toast-content-wrapper">
                    <h6 class="toast-title">BAST berhasil diunduh</h6>
                    <p class="toast-desc">File tersimpan di perangkat Anda.</p>
                </div>
                <button type="button" class="toast-close-btn" aria-label="Tutup" @click="showSuccessToast = false">
                    <i class="fas fa-times"></i>
                </button>
            </div>
        </transition>
    </div>
</template>

<script>
    import html2pdf from 'html2pdf.js'
    import SuratPengantar from './SuratPengantar.vue'
    import { formatCount, toBastFilename } from '@/services/qr-issue'

    const ZOOM_MIN = 50
    const ZOOM_MAX = 200
    const ZOOM_STEP = 25
    const TOAST_DURATION_MS = 3000

    export default {
        components : {
            SuratPengantar
        },
        props : {
            issueId : { type: [Number, String], required: true },
            bastNo : { type: String, default: '' },
            qty : { type: Number, default: 0 }
        },
        data () {
            return {
                ZOOM_MIN,
                ZOOM_MAX,
                ZOOM_STEP,
                status : 'loading',
                receipt : null,
                pdfBlob : null,
                zoom : 100,
                showSuccessToast : false,
                toastTimer : null,
                loadRequestId : 0,
            }
        },
        computed : {
            isLoaded () {
                return this.status === 'loaded'
            },
            filename () {
                return toBastFilename(this.bastNo)
            }
        },
        mounted () {
            this.loadDocument()
        },
        beforeDestroy () {
            clearTimeout(this.toastTimer)
        },
        methods : {
            formatCount,
            formatFileSize (bytes) {
                if (bytes < 1024) return `${bytes} B`
                if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
                return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
            },
            async loadDocument () {
                const requestId = ++this.loadRequestId
                this.status = 'loading'
                this.pdfBlob = null
                try {
                    const resp = await this.$http.get(`/api/qrs/issue/${this.issueId}`)
                    const receipt = resp.data ? resp.data.data : null
                    if (!receipt) throw new Error('Data BAST tidak ditemukan')
                    if (requestId !== this.loadRequestId) return

                    this.receipt = receipt
                    await this.$nextTick()
                    const blob = await html2pdf()
                        .set({
                            margin : 0,
                            image : { type: 'jpeg', quality: 0.98 },
                            html2canvas : { scale: 2, useCORS: true },
                            jsPDF : { unit: 'mm', format: 'a4', orientation: 'portrait' }
                        })
                        .from(this.$refs.pdfSource)
                        .outputPdf('blob')
                    if (requestId !== this.loadRequestId) return

                    this.pdfBlob = blob
                    this.status = 'loaded'
                } catch (err) {
                    console.log(err)
                    if (requestId === this.loadRequestId) this.status = 'failed'
                }
            },
            handleZoom (delta) {
                this.zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, this.zoom + delta))
            },
            handleDownload () {
                if (!this.isLoaded) return
                const url = URL.createObjectURL(this.pdfBlob)
                try {
                    const link = document.createElement('a')
                    link.href = url
                    link.download = this.filename
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                } finally {
                    URL.revokeObjectURL(url)
                }

                this.showSuccessToast = true
                clearTimeout(this.toastTimer)
                this.toastTimer = setTimeout(() => { this.showSuccessToast = false }, TOAST_DURATION_MS)
            }
        }
    }
</script>

<style scoped>
    .bast-modal-mask {
        position: fixed;
        z-index: 1060;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.4);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px;
    }

    .bast-modal-container {
        width: 800px;
        max-width: 100%;
        max-height: 100%;
        background-color: #FFFFFF;
        border-radius: 8px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    /* Header */
    .bast-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 16px 16px 16px 24px;
        border-bottom: 1px solid #F3F3F3;
    }

    .bast-heading {
        flex: 1;
        min-width: 0;
    }

    .bast-title {
        margin: 0 0 4px;
        font-size: 16px;
        font-weight: 600;
        color: #2F2F2F;
    }

    .bast-meta {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 4px 12px;
        font-size: 12px;
    }

    .bast-meta-item {
        display: flex;
        align-items: center;
        gap: 8px;
        white-space: nowrap;
    }

    .bast-meta-label {
        color: #707070;
    }

    .bast-meta-value {
        font-weight: 700;
        color: #2F2F2F;
    }

    .bast-meta-divider {
        width: 1px;
        height: 12px;
        background-color: #E0E0E0;
    }

    .btn-close-bast {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        padding: 0;
        background: transparent;
        border: none;
        border-radius: 4px;
        font-size: 14px;
        color: #707070;
        cursor: pointer;
    }

    .btn-close-bast:hover {
        background-color: #F8F8F8;
        color: #2F2F2F;
    }

    /* Viewer */
    .bast-body {
        position: relative;
        flex-shrink: 0;
        height: min(480px, 60vh);
        background-color: #F2F2F2;
    }

    .bast-viewer {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        overflow: auto;
        padding: 24px;
    }

    .bast-viewer::-webkit-scrollbar {
        width: 10px;
        height: 10px;
    }

    .bast-viewer::-webkit-scrollbar-track {
        background: transparent;
    }

    .bast-viewer::-webkit-scrollbar-thumb {
        background-color: #C4C4C4;
        background-clip: padding-box;
        border: 3px solid transparent;
        border-radius: 50px;
    }

    .bast-paper-wrapper {
        display: flex;
        justify-content: center;
        padding-bottom: 48px;
    }

    .bast-paper {
        flex-shrink: 0;
        width: 800px;
        background-color: #FFFFFF;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    }

    /* Floating zoom control */
    .bast-zoom {
        position: absolute;
        bottom: 16px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 6px 12px;
        background-color: rgba(47, 47, 47, 0.9);
        border-radius: 999px;
        font-size: 12px;
        line-height: 16px;
        color: #FFFFFF;
        white-space: nowrap;
    }

    .bast-zoom--disabled {
        opacity: 0.4;
    }

    .btn-zoom {
        padding: 0;
        background: transparent;
        border: none;
        font-size: 12px;
        line-height: 16px;
        color: #FFFFFF;
        cursor: pointer;
    }

    .btn-zoom:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    .zoom-separator {
        color: #707070;
    }

    /* Loading skeleton */
    .bast-skeleton {
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding: 48px;
        background-color: #FFFFFF;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    }

    .skeleton-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .skeleton-stack {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .skeleton-stack--end {
        align-items: flex-end;
    }

    .skeleton-bar {
        max-width: 100%;
        height: 12px;
        border-radius: 4px;
        background: linear-gradient(90deg, #EDEDED 25%, #F5F5F5 50%, #EDEDED 75%);
        background-size: 200% 100%;
        animation: bast-shimmer 1.2s infinite;
    }

    .skeleton-bar--logo { width: 120px; height: 28px; }
    .skeleton-bar--mark { width: 96px; height: 34px; }
    .skeleton-bar--title { width: 240px; height: 20px; }
    .skeleton-bar--subtitle { width: 160px; }
    .skeleton-bar--date { width: 180px; }
    .skeleton-bar--label { width: 80px; }
    .skeleton-bar--wide { width: 600px; }
    .skeleton-bar--short { width: 120px; }
    .skeleton-bar--medium { width: 140px; }

    .skeleton-divider {
        height: 1px;
        background-color: #F2F2F2;
    }

    @keyframes bast-shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
    }

    .bast-loading-badge {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        background-color: #FFFFFF;
        border-radius: 999px;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        font-size: 12px;
        color: #2F2F2F;
        white-space: nowrap;
    }

    .loading-spinner {
        width: 16px;
        height: 16px;
        border: 2px solid #FDE3D3;
        border-top-color: #F36F21;
        border-radius: 50%;
        animation: bast-spin 0.8s linear infinite;
    }

    @keyframes bast-spin {
        to { transform: rotate(360deg); }
    }

    /* Error state */
    .bast-error {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        height: 100%;
        text-align: center;
    }

    .error-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background-color: #EF5350;
        font-size: 16px;
        color: #FFFFFF;
    }

    .error-message {
        display: flex;
        flex-direction: column;
        gap: 4px;
        max-width: 240px;
    }

    .error-title {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        color: #2F2F2F;
    }

    .error-desc {
        margin: 0;
        font-size: 12px;
        color: #707070;
    }

    /* Footer */
    .bast-footer {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 8px 16px;
        padding: 16px 24px;
        border-top: 1px solid #F3F3F3;
    }

    .file-info {
        flex: 1;
        min-width: 0;
        font-size: 12px;
        color: #707070;
    }

    .file-separator {
        margin: 0 8px;
    }

    .bast-actions {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .btn-bast-secondary,
    .btn-bast-primary {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 8px 16px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 600;
        line-height: 16px;
        white-space: nowrap;
        cursor: pointer;
    }

    .btn-bast-secondary {
        background-color: #FFFFFF;
        border: 1px solid #E0E0E0;
        color: #2F2F2F;
    }

    .btn-bast-secondary:hover {
        background-color: #FAFAFA;
    }

    .btn-bast-primary {
        background-color: #F36F21;
        border: 1px solid #F36F21;
        color: #FFFFFF;
    }

    .btn-bast-primary:hover:not(:disabled) {
        background-color: #E06216;
    }

    .btn-bast-primary:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    .bast-pdf-source {
        position: fixed;
        top: 0;
        left: -10000px;
        pointer-events: none;
    }

    .bast-pdf-page {
        width: 800px;
        background-color: #FFFFFF;
    }

    /* Toast */
    .top-right-toast {
        position: fixed;
        bottom: 59px;
        right: 24px;
        z-index: 2000;
        width: 360px;
        max-width: calc(100% - 32px);
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 12px 16px;
        background-color: #FFFFFF;
        border: 1px solid #F3F3F3;
        border-radius: 8px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }

    .toast-icon-wrapper {
        display: flex;
        flex-shrink: 0;
    }

    .toast-content-wrapper {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 4px;
        font-size: 12px;
    }

    .toast-title {
        margin: 0;
        font-size: 12px;
        font-weight: 600;
        color: #2F2F2F;
    }

    .toast-desc {
        margin: 0;
        font-size: 12px;
        color: #707070;
    }

    .toast-close-btn {
        flex-shrink: 0;
        padding: 0;
        background: transparent;
        border: none;
        font-size: 10px;
        line-height: 20px;
        color: #707070;
        cursor: pointer;
    }

    .toast-slide-enter-active,
    .toast-slide-leave-active {
        transition: all 0.25s ease;
    }

    .toast-slide-enter,
    .toast-slide-leave-to {
        opacity: 0;
        transform: translateY(8px);
    }
</style>
